import React, { useEffect, useState } from 'react';
import { Dialog, useToast } from '../../components/shared/Overlay';
import { Field, Input, Select, Switch, Textarea } from '../../components/shared/Form';
import { Alert } from '../../components/shared/Feedback';
import { useAuth } from '../../contexts/AuthContext';
import { errorMessage, reportsAPI } from '../../services/reportsApi';
import { useAccountTimeZone, useTerms } from '../../layout/ShellStatus';
import { buildCron, clockText, describeCron, groupKey, isEmailAddress, parseCron, parseRecipients } from './reportsLogic';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(n).padStart(2, '0');

function initialState(draft) {
  const cron = parseCron(draft.cronExpression || '0 7 1 * *');
  return {
    name: draft.name || '',
    savedReportId: draft.savedReportId || '',
    newBoardReport: draft.newBoardReport || null,
    format: draft.format || 'Pdf',
    recipients: (draft.recipients || []).join('\n'),
    groups: (draft.recipientGroups || []).map(groupKey),
    enabled: draft.id ? Boolean(draft.enabled) : true,
    ...cron,
    time: `${pad(cron.hour)}:${pad(cron.minute)}`,
  };
}

/**
 * New or edit schedule (contracts §7 reports/schedules): saved report, cron-like picker in the account time zone, recipients,
 * format, enabled. Saving makes the signed-in user the schedule's run identity (A023): runs use that person's
 * access, recipients must be people with a Vertolytics sign-in, and recipient groups must see at least the same
 * data. While the email kill switch is on, runs render files and send nothing. A board-summary draft
 * (`newBoardReport`) first saves the community summary as a saved report.
 */
export function ScheduleDialog({ draft, saved, delivery, directory, onClose, onSaved }) {
  const toast = useToast();
  const terms = useTerms();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const { zone, label: zoneLabel } = useAccountTimeZone();

  useEffect(() => {
    setState(draft ? initialState(draft) : null);
    setError(null);
  }, [draft]);

  if (!draft || !state) return null;

  const set = (patch) => setState((s) => ({ ...s, ...patch }));
  const editing = Boolean(draft.id);
  const runsAs = draft.runAsUserId || draft.createdByUserId;
  const runsAsMe = !editing || !runsAs || runsAs === user?.id;
  const [hour, minute] = state.time.split(':').map(Number);
  const cron = buildCron({ ...state, hour, minute });

  const submit = async () => {
    const recipients = parseRecipients(state.recipients);
    if (!state.name.trim()) return setError('Give the schedule a name');
    const notAnAddress = recipients.find((r) => !isEmailAddress(r));
    if (notAnAddress) return setError(`“${notAnAddress}” isn't an email address. Enter one address per line.`);
    if (!state.savedReportId && !state.newBoardReport) return setError('Choose a saved report');
    if (!cron) return setError('Enter a cron expression');
    setSaving(true);
    setError(null);
    try {
      let savedReportId = state.savedReportId;
      if (!savedReportId && state.newBoardReport) savedReportId = (await reportsAPI.createSaved(state.newBoardReport)).id;
      const known = Object.fromEntries((directory?.groups || []).map((g) => [groupKey(g), g]));
      const kept = (draft.recipientGroups || []).filter((g) => state.groups.includes(groupKey(g)));
      const recipientGroups = [
        ...kept,
        ...state.groups.filter((key) => !kept.some((g) => groupKey(g) === key) && known[key]).map((key) => ({ role: known[key].role, communityId: known[key].communityId, hierarchyNodeId: known[key].hierarchyNodeId })),
      ];
      const body = { ...(editing ? draft : {}), name: state.name.trim(), savedReportId, cronExpression: cron, timeZone: zone, recipients, recipientGroups, format: state.format, enabled: state.enabled };
      if (editing) await reportsAPI.updateSchedule(draft.id, body);
      else await reportsAPI.createSchedule(body);
      toast(editing ? `Updated “${body.name}”` : `Scheduled “${body.name}”`);
      onSaved();
    } catch (e) {
      setError(await errorMessage(e, "The schedule couldn't be saved"));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button type="button" className="btn" onClick={onClose}>
        Cancel
      </button>
      <button type="button" className="btn btn-primary" onClick={submit} disabled={saving}>
        {editing ? 'Save schedule' : 'Create schedule'}
      </button>
    </>
  );

  return (
    <Dialog open onClose={onClose} title={editing ? 'Edit schedule' : 'New schedule'} footer={footer}>
      <div className="stack">
        {error && (
          <Alert tone="error" title="The schedule wasn't saved">
            {error}
          </Alert>
        )}
        <Field label="Name" htmlFor="sd-name">
          <Input id="sd-name" value={state.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        {state.newBoardReport ? (
          <Field label="Report">
            <div className="small">{state.newBoardReport.name} (board summary, previous month)</div>
          </Field>
        ) : (
          <Field label="Saved report" htmlFor="sd-report">
            <Select
              id="sd-report"
              value={state.savedReportId}
              onChange={(e) => {
                const report = saved.find((r) => r.id === e.target.value);
                set({ savedReportId: e.target.value, name: state.name || report?.name || '' });
              }}
            >
              <option value="">Choose a saved report</option>
              {saved.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Runs" htmlFor="sd-frequency" hint={state.frequency === 'custom' ? `Five fields: minute hour day-of-month month day-of-week, ${zoneLabel}` : describeCron(cron)}>
          <div className="row-wrap">
            <Select id="sd-frequency" value={state.frequency} onChange={(e) => set({ frequency: e.target.value })} style={{ width: 140 }}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="custom">Custom (cron)</option>
            </Select>
            {state.frequency === 'weekly' && (
              <Select aria-label="Day of week" value={state.weekday} onChange={(e) => set({ weekday: Number(e.target.value) })} style={{ width: 150 }}>
                {WEEKDAYS.map((day, i) => (
                  <option key={day} value={i}>
                    {day}
                  </option>
                ))}
              </Select>
            )}
            {state.frequency === 'monthly' && (
              <Select aria-label="Day of month" value={state.monthDay} onChange={(e) => set({ monthDay: Number(e.target.value) })} style={{ width: 110 }}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    Day {d}
                  </option>
                ))}
              </Select>
            )}
            {state.frequency === 'custom' ? (
              <Input aria-label="Cron expression" value={state.cron} onChange={(e) => set({ cron: e.target.value })} placeholder="0 7 1 * *" style={{ width: 180 }} />
            ) : (
              <Input type="time" aria-label={`Time (${clockText(hour || 0, minute || 0)} ${zoneLabel.replace(/ Time$/, '')})`} value={state.time} onChange={(e) => set({ time: e.target.value || '07:00' })} style={{ width: 130 }} />
            )}
          </div>
        </Field>
        <Field label="Format" htmlFor="sd-format">
          <Select id="sd-format" value={state.format} onChange={(e) => set({ format: e.target.value })}>
            <option value="Pdf">PDF</option>
            <option value="Xlsx">Excel workbook (.xlsx)</option>
            <option value="Csv">CSV (.csv)</option>
          </Select>
        </Field>
        {directory?.groups?.length > 0 && (
          <Field label="Recipient groups" hint={`Members are looked up each time the schedule runs. The report is computed with your access, so choose groups whose members can see the same ${terms.communitiesLower} as you.`}>
            <div className="stack" style={{ gap: 6 }}>
              {directory.groups.map((g) => {
                const key = groupKey(g);
                return (
                  <label className="check" key={key}>
                    <input
                      type="checkbox"
                      checked={state.groups.includes(key)}
                      onChange={(e) => set({ groups: e.target.checked ? [...state.groups, key] : state.groups.filter((k) => k !== key) })}
                    />
                    {g.name} ({g.memberCount})
                  </label>
                );
              })}
            </div>
          </Field>
        )}
        <Field label="Other recipients" htmlFor="sd-recipients" hint={`Email addresses of people with a Vertolytics sign-in who can see the same ${terms.communitiesLower} as you, one per line`}>
          <Textarea id="sd-recipients" rows={3} value={state.recipients} onChange={(e) => set({ recipients: e.target.value })} />
        </Field>
        <Switch label="Enabled" checked={state.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        <p className="caption">
          {runsAsMe
            ? `Runs with your access: it includes only the ${terms.communitiesLower} and call content you can see.`
            : 'Runs with the access of the person who saved it last. When you save, it runs with your access instead. If that person leaves or loses access, runs fail and show why here.'}
          {delivery && !delivery.emailEnabled ? ' Email delivery is off: runs render the file and send nothing.' : ''}
        </p>
      </div>
    </Dialog>
  );
}
